import React from 'react'
import {set} from 'lodash'
import {strftime} from '@fotolog/xutil'
import axios from 'axios'
import {useLocation} from 'wouter'
import {useSuspense, useController} from '@data-client/react'
import {apiReply, toast, useAppNav, useForm, SessionState, checkForUpgrade, getDataAttr} from '../lib'
import {User, MemberResource, TeamResource} from '../lib/model'
import {ActionHeader, AppMsg, Field, PanelProps, SubmitButton, UserNoCanDo} from '.'
import ImageUploader from './ImageUploader'
import {AddTeamMemberSchema, UpdateUserSchema} from '../lib/schema/Profile'
import {AddTeamMemberForm, UpdateUserForm} from '../lib/form/Profile'
import {
  Avatar,
  Box,
  Button,
  Divider,
  Fab,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import {
  IconUpdate,
  IconDelete,
  IconTripMap,
  IconAddMember,
} from '../Icons'
import {UpdateTeamSchema} from '../lib/schema/Team'
import {UpdateTeamForm} from '../lib/form/Team'

export function TeamSettings({session}: PanelProps) {
  const api = useController()
  const form = useForm(UpdateTeamSchema, {defaults: session.getSettings()})
  React.useEffect(() => {
    // can't use defaultValues since doesn't get re-evaluated
    form.reset(session.getSettings())
  }, [session.scope.teamId]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!session.scope.isTeamLead) {
    return <UserNoCanDo />
  }

  /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
  /* @ts-expect-error */
  const onSubmit = form.handleSubmit(async (data: UpdateTeamSchema) => {
    const msg = toast('loading', 'Updating Team Settings')
    const reply = await apiReply(() => api.fetch(TeamResource.update, {id: session.scope.teamId}, data))
    if (reply.error) {
      msg.fail(reply.error)
      return
    }
    session.update((state: SessionState) => {
      // apply theme
      set(state, 'scope.team.theme.primary', data.theme.primary)
      return state
    })
    msg.succeed()
  })

  return (
    <form onSubmit={onSubmit}>
      <Box my={5}>
        <Field form={form} attrs={UpdateTeamForm.name} />
      </Box>
      <Box my={5}>
        <Field form={form} attrs={UpdateTeamForm.theme.primary} />
      </Box>
      <Box mt={3} textAlign="center">
        <SubmitButton form={form}>Save Changes</SubmitButton>
      </Box>
    </form>
  )
}

export function MemberHelp() {
  return (
    <div className="app-flex justify-content-start alert alert-primary mt-5 p-4 w-100">
      <IconAddMember />
      <div>No members have been added to the team yet. Click the &quot;Add&quot; button at the top to add one.</div>
    </div>
  )
}

export function AddMember({session}: PanelProps) {
  const nav = useAppNav()
  const api = useController()
  const form = useForm(AddTeamMemberSchema)

  /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
  /* @ts-expect-error */
  const onSubmit = form.handleSubmit(async (data: AddTeamMemberSchema) => {
    const msg = toast('loading', 'Adding Team Member')
    const teamId = session.scope.teamId
    const reply = await apiReply(() => api.fetch(MemberResource.create, {teamId, ...data}))
    if (reply.error) {
      msg.fail(reply.error)
      return
    }
    msg.succeed()
    // api.invalidate(MemberResource.getList, {scope: session.euid()})
    api.invalidate(MemberResource.getList, {teamId})
    nav.back()
  })

  return (
    <form onSubmit={onSubmit} autoComplete="off">
      <Box my={5}>
        <Field form={form} attrs={AddTeamMemberForm.name} />
      </Box>
      <Box my={5}>
        <Field form={form} attrs={AddTeamMemberForm.email} />
      </Box>
      <Box mt={3} textAlign="center">
        <SubmitButton form={form}>Add Team Member</SubmitButton>
      </Box>
    </form>
  )
}

type MemberRowProps = {
  user: User
}
export function MemberRow({user}: MemberRowProps) {
  return (
    <TableRow data-id={user.id} hover className="app-clickable" sx={{'&:last-child td, &:last-child th': {border: 0}}}>
      <TableCell>
        <Box display="flex" alignItems="center">
          <Avatar alt={user.name} src={user.avatar} sx={{width: 50, height: 50, mr: 2}} />
          <div className="app-truncate">
            {user.name}
            <br />
            {user.email}
          </div>
        </Box>
      </TableCell>
      <TableCell className="app-hide-sm" align="right">
        {strftime(user.created, 'PP')}
      </TableCell>
    </TableRow>
  )
}

type MembersTableProps = {
  users: User[]
}
export function MembersTable({users}: MembersTableProps) {
  const setLocation = useLocation()[1]
  const onDetails = (evt: React.SyntheticEvent<HTMLElement>) => {
    const id = getDataAttr('id', evt.target as HTMLElement)
    setLocation(`/team/member/${id}/view`)
  }
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align="left">Contact Info</TableCell>
            <TableCell className="app-hide-sm" align="right">
              Created
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody onClick={onDetails}>
          {users.map(i => (
            <MemberRow key={i.id} user={i} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export function UpdateMember({session, params}: PanelProps) {
  const nav = useAppNav()
  const api = useController()
  // const member = useSuspense(MemberResource.get, {teamId: session.scope.teamId, id: params['id'] || session.euid()})
  const member = useSuspense(MemberResource.get, {id: params['id'] || session.euid()})
  const form = useForm(UpdateUserSchema, {defaults: member})

  /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
  /* @ts-expect-error */
  const onSubmit = form.handleSubmit(async (data: UpdateUserSchema) => {
    const msg = toast('loading', 'Updating Team Member')
    const reply = await apiReply(() => api.fetch(MemberResource.update, {id: member.id}, data))
    if (reply.error) {
      msg.fail(reply.error)
      return
    }
    const newScope = {
      ...session.scope,
      pk: () => String(session.euid()),
      name: data.name,
      email: data.email,
    }
    session.update(state => {
      state.scope = newScope
      return state
    })
    localStorage.setItem('session.scope', JSON.stringify(newScope))
    if (!session.isManaging()) {
      session.update(state => {
        state.user.name = data.name
        state.user.email = data.email
        return state
      })
    }
    msg.succeed()
    checkForUpgrade()
    nav.back()
  })

  return (
    <form onSubmit={onSubmit} autoComplete="off">
      <Box my={5}>
        <Field form={form} attrs={UpdateUserForm.name} />
      </Box>
      <Box my={5}>
        <Field form={form} attrs={UpdateUserForm.email} />
      </Box>
      <Box my={5}>
        <Field form={form} attrs={UpdateUserForm.password} />
      </Box>
      <Box mt={3} textAlign="center">
        <SubmitButton form={form}>Save</SubmitButton>
      </Box>
    </form>
  )
}

export function ViewMember({session, params}: PanelProps) {
  const member = useSuspense(MemberResource.get, {id: params['id']})

  return (
    <>
      <ActionHeader title={member.name}>
        <Button href={`../update`} color="primary">
          <IconUpdate />
        </Button>
        <Divider orientation="vertical" variant="middle" flexItem />
        <Button href={`../delete`} color="primary">
          <IconDelete />
        </Button>
      </ActionHeader>
      <Paper elevation={3} sx={{p: 2, my: 3}}>
        <dl className="app-keyval">
          <dt>Name</dt>
          <dd>{member.name}</dd>
          <dt>Email</dt>
          <dd>{member.email}</dd>
          <dt>Last Active</dt>
          <dd>{member.lastActive ? strftime(member.lastActive, 'PPP') : 'NEVER'}</dd>
          <dt>Trips</dt>
          <dd>
            <IconButton href={`/globe?scope=${member.id}`} color="primary">
              <IconTripMap />
            </IconButton>
          </dd>
          <dt>Created</dt>
          <dd>{strftime(member.created, 'PPP')}</dd>
        </dl>
      </Paper>
    </>
  )
}

export function ViewProfile({session}: PanelProps) {
  const member = useSuspense(MemberResource.get, {id: session.euid()})

  const onNewAvatar = async (image: Blob) => {
    const msg = toast('loading', 'Uploading Fotos')
    const form = new FormData()
    form.append('image', image)
    const reply = await axios.post(`/api/team-member/${member.id}/avatar`, form, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'App-User': `${session.id}`, // helps with trouble-shooting
      },
    })
    msg.succeed()
    session.update(state => {
      state.scope.avatar = reply.data.avatar
      return state
    })
  }

  return (
    <>
      <Typography variant="h1">Avatar</Typography>
      <ImageUploader value={member.avatar} height={100} width={100} onChange={onNewAvatar} />

      <Typography variant="h1" mt={5} mb={2}>
        Profile
        <Fab href={`../update-profile`} color="inherit" size="medium" sx={{ml: 2}}>
          <IconUpdate />
        </Fab>
      </Typography>
      <Paper elevation={3} sx={{py: 1, px: 2}}>
        <dl className="app-keyval">
          <dt>Name</dt>
          <dd>{member.name}</dd>
          <dt>Email</dt>
          <dd>{member.email}</dd>
        </dl>
      </Paper>
    </>
  )
}

export function DeleteMember({session, params}: PanelProps) {
  const nav = useAppNav()
  const api = useController()
  const member = useSuspense(MemberResource.get, {id: session.euid()})

  const onSubmit = async () => {
    const msg = toast('loading', 'Deleting Team Member')
    const reply = await apiReply(() => api.fetch(MemberResource.delete, {id: params['id']}))
    if (reply.error) {
      msg.fail(reply.error)
      return
    }
    msg.succeed()
    nav.to(params['home'])
  }

  return (
    <>
      <Typography variant="h3" my={3}><span className='app-subdued'>Member:</span> {member.name}</Typography>
      <AppMsg severity="error" size="medium">
        This action can't be undone.<br/><br/>Are you sure?
      </AppMsg>
      <Box mt={3} display="flex" justifyContent="center">
        <Button color="secondary" sx={{mr: 2}} onClick={() => nav.back()}>
          Cancel
        </Button>
        <Button color="primary" onClick={onSubmit}>
          Yes, Delete
        </Button>
      </Box>
    </>
  )
}
